import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { routerActions } from 'connected-react-router';
import { Loc } from 'redux-react-i18n';
import { Switch, Route } from 'react-router-dom';
import LazyLoad from 'react-lazyload';
import ContentSimpleGrid from '../../components/Filters/ContentSimpleGrid';
import Slider from '../../components/Slider';
import PageTitle from '../../components/PageTitle';
import MediasCarousel from '../../components/Carousels/MediasCarousel';
import ContentFilter from '../../components/Filters/ContentFilter';
import MovieDetailsPage from './MovieDetailsPage';
import * as slidersActions from '../../actions/slidersActions';
import * as moviesActions from '../../actions/moviesActions';
import * as mediaDetailsActions from '../../actions/mediaDetailsActions';

class MoviesPage extends React.Component {
  static propTypes = {
    // Actions
    routerActions: PropTypes.shape({
      push: PropTypes.func,
    }).isRequired,
    slidersActions: PropTypes.shape({
      getMoviesSlides: PropTypes.func,
    }).isRequired,
    moviesActions: PropTypes.shape({
      getMoviesFilter: PropTypes.func,
      getFilteredMovies: PropTypes.func,
      clearFilteredMovies: PropTypes.func,
      getNewMovies: PropTypes.func.isRequired,
      getLastMovies: PropTypes.func.isRequired,
      getTopMovies: PropTypes.func.isRequired,
      getRecommendedMovies: PropTypes.func.isRequired,
    }).isRequired,
    mediaDetailsActions: PropTypes.shape({
      getMediaDetails: PropTypes.func.isRequired,
    }).isRequired,

    // Data
    currentLanguage: PropTypes.string.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }).isRequired,
    moviesSlidersData: PropTypes.shape({
      status: PropTypes.number,
      data: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    moviesData: PropTypes.shape({
      new: PropTypes.shape({
        status: PropTypes.number,
        data: PropTypes.arrayOf(PropTypes.object),
      }),
    }).isRequired,
    contentDetails: PropTypes.shape({
      mediaDetails: PropTypes.shape({
        status: PropTypes.number,
        data: PropTypes.objectOf(PropTypes.oneOfType([
          PropTypes.bool,
          PropTypes.string,
          PropTypes.number,
          PropTypes.array,
          PropTypes.object,
        ])),
      }),
    }).isRequired,
    filterData: PropTypes.shape({
      status: PropTypes.number,
      data: PropTypes.objectOf(PropTypes.array),
    }).isRequired,
    filteredMoviesData: PropTypes.shape({
      status: PropTypes.number,
      pageLoaded: PropTypes.number,
      data: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
  };

  constructor() {
    super();

    this.state = {
      genresIds: [],
      countriesIds: [],
      yearsIds: [],
      languagesIds: [],
      resetPageCounter: false,
    };

    this.formHandler = this.formHandler.bind(this);
    this.resetBtnHandler = this.resetBtnHandler.bind(this);
    this.genresCheckboxHandler = this.genresCheckboxHandler.bind(this);
    this.countriesCheckboxHandler = this.countriesCheckboxHandler.bind(this);
    this.yearsCheckboxHandler = this.yearsCheckboxHandler.bind(this);
    this.languagesCheckboxHandler = this.languagesCheckboxHandler.bind(this);
    this.filteredContentLoadHandler = this.filteredContentLoadHandler.bind(this);
    this.newMoviesLoadHandler = this.newMoviesLoadHandler.bind(this);
    this.lastMoviesLoadHandler = this.lastMoviesLoadHandler.bind(this);
    this.topMoviesLoadHandler = this.topMoviesLoadHandler.bind(this);
    this.recommendedMoviesLoadHandler = this.recommendedMoviesLoadHandler.bind(this);
  }

  resetBtnHandler() {
    this.props.routerActions.push('/movies');
    this.setState({
      genresIds: [],
      countriesIds: [],
      yearsIds: [],
      languagesIds: [],
    });
  }

  formHandler() {
    if (this.props.location.pathname !== '/movies/filtered') {
      this.props.routerActions.push('/movies/filtered');
    }
  }

  genresCheckboxHandler(e) {
    const currentTarget = e.target;

    this.props.moviesActions.clearFilteredMovies();
    this.setState({
      resetPageCounter: true,
    });

    const { genresIds } = this.state;

    if (genresIds.indexOf(currentTarget.value) !== -1) {
      this.setState({
        genresIds: genresIds.filter(genre => genre !== currentTarget.value),
      });
    } else {
      this.setState(prevState => ({ genresIds: [...prevState.genresIds, currentTarget.value] }));
    }
  }

  countriesCheckboxHandler(e) {
    const currentTarget = e.target;

    this.props.moviesActions.clearFilteredMovies();
    this.setState({
      resetPageCounter: true,
    });

    const { countriesIds } = this.state;

    if (countriesIds.indexOf(currentTarget.value) !== -1) {
      this.setState({
        countriesIds: countriesIds.filter(country => country !== currentTarget.value),
      });
    } else {
      this.setState(prevState => ({
        countriesIds: [...prevState.countriesIds, currentTarget.value],
      }));
    }
  }

  yearsCheckboxHandler(e) {
    const currentTarget = e.target;

    this.props.moviesActions.clearFilteredMovies();
    this.setState({
      resetPageCounter: true,
    });

    const { yearsIds } = this.state;

    if (yearsIds.indexOf(currentTarget.value) !== -1) {
      this.setState({
        yearsIds: yearsIds.filter(year => year !== currentTarget.value),
      });
    } else {
      this.setState(prevState => ({ yearsIds: [...prevState.yearsIds, currentTarget.value] }));
    }
  }

  languagesCheckboxHandler(e) {
    const currentTarget = e.target;

    this.props.moviesActions.clearFilteredMovies();
    this.setState({
      resetPageCounter: true,
    });

    const { languagesIds } = this.state;

    if (languagesIds.indexOf(currentTarget.value) !== -1) {
      this.setState({
        languagesIds: languagesIds.filter(language => language !== currentTarget.value),
      });
    } else {
      this.setState(prevState => ({
        languagesIds: [...prevState.languagesIds, currentTarget.value],
      }));
    }
  }

  filteredContentLoadHandler(pageNum) {
    const {
      genresIds,
      countriesIds,
      yearsIds,
      languagesIds,
    } = this.state;

    if (this.state.resetPageCounter) {
      this.setState({
        resetPageCounter: false,
      });
    }

    this.props.moviesActions.getFilteredMovies(
      pageNum,
      genresIds,
      countriesIds,
      yearsIds,
      languagesIds,
    );
  }

  newMoviesLoadHandler(page) {
    this.props.moviesActions.getNewMovies(page);
  }

  lastMoviesLoadHandler(page) {
    this.props.moviesActions.getLastMovies(page);
  }

  topMoviesLoadHandler(page) {
    this.props.moviesActions.getTopMovies(page);
  }

  recommendedMoviesLoadHandler(page) {
    this.props.moviesActions.getRecommendedMovies(page);
  }

  render() {
    const {
      currentLanguage,
      moviesData,
      moviesSlidersData,
      contentDetails,
      filterData,
      filteredMoviesData,
    } = this.props;

    const {
      genresIds,
      countriesIds,
      yearsIds,
      languagesIds,
    } = this.state;

    const checkedData = {
      genresIds,
      countriesIds,
      yearsIds,
      languagesIds,
    };

    return (
      <React.Fragment>
        <Switch>
          <Route path="/movies/movie/:id" component={MovieDetailsPage} />
          <React.Fragment>
            {/* Filter */}
            <ContentFilter
              title={<Loc locKey="moviesGenres" />}
              getFilter={this.props.moviesActions.getMoviesFilter}
              filterData={filterData}
              currentLanguage={currentLanguage}
              formHandler={this.formHandler}
              resetBtnHandler={this.resetBtnHandler}
              checkedData={checkedData}
              genresCheckboxHandler={this.genresCheckboxHandler}
              countriesCheckboxHandler={this.countriesCheckboxHandler}
              yearsCheckboxHandler={this.yearsCheckboxHandler}
              languagesCheckboxHandler={this.languagesCheckboxHandler}
            />

            <Route
              path="/movies/filtered"
              render={
                () => (
                  <ContentSimpleGrid
                    itemRoute="movie"
                    contentData={filteredMoviesData}
                    resetPageCounter={this.state.resetPageCounter}
                    handleInfiniteLoad={this.filteredContentLoadHandler}
                    currentLanguage={currentLanguage}
                  />
                )
              }
            />

            <Route
              path="/movies/new"
              render={
                () => (
                  <React.Fragment>
                    <PageTitle level={2}>
                      <Loc locKey="new" />
                    </PageTitle>
                    <ContentSimpleGrid
                      itemRoute="movie"
                      contentData={moviesData.new}
                      resetPageCounter={this.state.resetPageCounter}
                      handleInfiniteLoad={this.newMoviesLoadHandler}
                      currentLanguage={currentLanguage}
                    />
                  </React.Fragment>
                )
              }
            />

            <Route
              path="/movies/last"
              render={
                () => (
                  <React.Fragment>
                    <PageTitle level={2}>
                      <Loc locKey="last" />
                    </PageTitle>
                    <ContentSimpleGrid
                      itemRoute="movie"
                      contentData={moviesData.last}
                      resetPageCounter={this.state.resetPageCounter}
                      handleInfiniteLoad={this.lastMoviesLoadHandler}
                      currentLanguage={currentLanguage}
                    />
                  </React.Fragment>
                )
              }
            />

            <Route
              path="/movies/top"
              render={
                () => (
                  <React.Fragment>
                    <PageTitle level={2}>
                      <Loc locKey="top" />
                    </PageTitle>
                    <ContentSimpleGrid
                      itemRoute="movie"
                      contentData={moviesData.top}
                      resetPageCounter={this.state.resetPageCounter}
                      handleInfiniteLoad={this.topMoviesLoadHandler}
                      currentLanguage={currentLanguage}
                    />
                  </React.Fragment>
                )
              }
            />

            <Route
              path="/movies/recommended"
              render={
                () => (
                  <React.Fragment>
                    <PageTitle level={2}>
                      <Loc locKey="recommended" />
                    </PageTitle>
                    <ContentSimpleGrid
                      itemRoute="movie"
                      contentData={moviesData.recommended}
                      resetPageCounter={this.state.resetPageCounter}
                      handleInfiniteLoad={this.recommendedMoviesLoadHandler}
                      currentLanguage={currentLanguage}
                    />
                  </React.Fragment>
                )
              }
            />

            <Route
              exact
              path="/movies"
              render={
                () => (
                  <React.Fragment>
                    {/* Movies slider */}
                    <LazyLoad height={400} once debounce={50}>
                      <Slider
                        getSlides={this.props.slidersActions.getMoviesSlides}
                        data={moviesSlidersData.data}
                        accentColor="#6ab"
                        currentLanguage={currentLanguage}
                      />
                    </LazyLoad>

                    {/* New Movies container */}
                    <LazyLoad height={350} once debounce={50}>
                      <MediasCarousel
                        title={<Loc locKey="new" />}
                        buttonContent={<Loc locKey="watchAll" />}
                        titleRoute="movies/new"
                        itemRoute="movies/movie"
                        getMedias={this.props.moviesActions.getNewMovies}
                        getMediaDetails={this.props.mediaDetailsActions.getMediaDetails}
                        mediasData={moviesData.new}
                        mediaDetailsData={contentDetails.mediaDetails}
                        currentLanguage={currentLanguage}
                      />
                    </LazyLoad>

                    {/* Last Movies container */}
                    <LazyLoad height={350} once debounce={50}>
                      <MediasCarousel
                        title={<Loc locKey="last" />}
                        buttonContent={<Loc locKey="watchAll" />}
                        titleRoute="movies/last"
                        itemRoute="movies/movie"
                        getMedias={this.props.moviesActions.getLastMovies}
                        getMediaDetails={this.props.mediaDetailsActions.getMediaDetails}
                        mediasData={moviesData.last}
                        mediaDetailsData={contentDetails.mediaDetails}
                        currentLanguage={currentLanguage}
                      />
                    </LazyLoad>

                    {/* Top Movies container */}
                    <LazyLoad height={350} once debounce={50}>
                      <MediasCarousel
                        title={<Loc locKey="top" />}
                        buttonContent={<Loc locKey="watchAll" />}
                        titleRoute="movies/top"
                        itemRoute="movies/movie"
                        getMedias={this.props.moviesActions.getTopMovies}
                        getMediaDetails={this.props.mediaDetailsActions.getMediaDetails}
                        mediasData={moviesData.top}
                        mediaDetailsData={contentDetails.mediaDetails}
                        currentLanguage={currentLanguage}
                      />
                    </LazyLoad>

                    {/* Recommended Movies container */}
                    <LazyLoad height={350} once debounce={50}>
                      <MediasCarousel
                        title={<Loc locKey="recommended" />}
                        buttonContent={<Loc locKey="watchAll" />}
                        titleRoute="movies/recommended"
                        itemRoute="movies/movie"
                        getMedias={this.props.moviesActions.getRecommendedMovies}
                        getMediaDetails={this.props.mediaDetailsActions.getMediaDetails}
                        mediasData={moviesData.recommended}
                        mediaDetailsData={contentDetails.mediaDetails}
                        currentLanguage={currentLanguage}
                      />
                    </LazyLoad>
                  </React.Fragment>
                )
              }
            />
          </React.Fragment>
        </Switch>
      </React.Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentLanguage: state.i18n.currentLanguage,
    moviesSlidersData: state.sliders.movies,
    moviesData: state.movies,
    contentDetails: state.contentDetails,
    filterData: state.movies.availableFilters,
    filteredMoviesData: state.movies.filteredData,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    routerActions: bindActionCreators(routerActions, dispatch),
    slidersActions: bindActionCreators(slidersActions, dispatch),
    moviesActions: bindActionCreators(moviesActions, dispatch),
    mediaDetailsActions: bindActionCreators(mediaDetailsActions, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MoviesPage);
